import { head, omit, pipe, set } from 'lodash/fp';
import qs from 'qs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';
import { getFormValues } from 'redux-form';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  FORM_THEME_EDITOR_ADVANCED_NAME,
  FORM_THEME_EDITOR_BUTTONS_NAME,
  FORM_THEME_EDITOR_COLOR_NAME,
  FORM_THEME_EDITOR_COMPLETION_PAGE_NAME,
  FORM_THEME_EDITOR_DOCUMENTS_UPLOAD_PAGE_NAME,
  FORM_THEME_EDITOR_FAILED_PAGE_NAME,
  FORM_THEME_EDITOR_FONT_NAME,
  FORM_THEME_EDITOR_SELECT_COUNTRY_PAGE_NAME,
  FORM_THEME_EDITOR_START_PAGE_NAME,
} from 'dashboard/lib/constants/formNames';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { mergeResourceWithFormValues } from 'dashboard/lib/utils/formData';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Template } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import { setLocale, setSelectedTemplateId } from 'dashboard/redux/actions/editor';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { setTemplate, setTheme } from 'dashboard/redux/actions/organization';
import { isThemeEditorFormDirty } from 'dashboard/redux/selectors/theme';
import { RootState } from 'dashboard/redux/store';
import {
  loadNewAndLegacyInquiryTemplateList,
  loadThemeList,
} from 'dashboard/redux/thunk-actions/organization';
import DashboardEditorView from 'dashboard/views/DashboardEditorView';
import { readFile } from 'lib/file';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { fetchAndAddTranslation } from 'lib/translations';
import { dataUrlToBase64 } from 'lib/utils/dataUrl';

import 'lib/initializers/i18n';

type ThemeWithLogoFile = Theme & { attributes: { 'logo-file': File } };

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const processThemeLogo = async (theme: ThemeWithLogoFile): Promise<Theme> => {
  try {
    const file: File | null = theme.attributes['logo-file'];
    const logoData = file
      ? await new Promise<string>((res) => {
          const imgBuffer = document.createElement('img');
          imgBuffer.onload = async () => {
            res(dataUrlToBase64(imgBuffer.src));
          };

          readFile(file).then((result) => {
            imgBuffer.src = result as string;
          });
        })
      : null;

    // We need to coerce because a ThemeWithLogoFile can't be directly changed to a Theme
    return pipe(
      (t) => omit('attributes.logo-file', t) as Theme,
      (t) =>
        logoData
          ? (set('attributes.logo-data', logoData, t) as Theme)
          : (omit('attributes.logo-data', t) as Theme),
      (t) =>
        logoData
          ? (set('attributes.logo-filename', file.name, t) as Theme)
          : (omit('attributes.logo-filename', t) as Theme),
    )(theme);
  } catch (e) {
    // TODO
  }
};

const removeThemeLogo = async (theme: Theme) => {
  return SessionClientDashboard.postResourceAction<Theme>(
    {
      type: ApiModel.Theme,
      id: theme.id,
    },
    'remove-logo',
  );
};

const DashboardEditorRoute = (props: PropsFromRedux) => {
  const productFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.LegacyInquiryTemplates,
    props.currentEnvironment.type,
  );

  const { search: locationSearch, pathname: locationPathname } = useLocation();
  const {
    'selected-template-id': paramSelectedTemplateId,
    'selected-theme-id': paramSelectedThemeId,
  } = qs.parse(locationSearch, { ignoreQueryPrefix: true });

  const [t, i18n] = useTranslation();
  // Only used to get templateSelectedPristine to update
  const [customTranslations, setCustomTranslations] = useState(null);

  const selectedTemplate =
    props.templates.find((t) => t.id === props.selectedTemplateId) ||
    props.templates.find((t) => t.id === paramSelectedTemplateId) ||
    head(props.templates);

  const templateSelectedPristine = useMemo(() => {
    // IMPORTANT: [PER-7854] This prevents old templates with values in the
    // legacy translation attributes from losing their new copy when templates
    // are updated in the theme editor.
    //
    // Here, we write the values from the {locale}.json file to the form. This
    // prevents "legacy" translation values from overwriting subsequent changes
    // made to the JSON, and allows changes made to the JSON to display in the
    // editor if legacy translations already exist.
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const templateWithPatchedCopy = mergeResourceWithFormValues<Template>(selectedTemplate, {
      'intro-title': t('intro.title'),
      'intro-prompt': t('intro.prompt'),
      'intro-btn-submit': t('intro.btn-submit'),
      'select-country-title': t('select-country.title'),
      'select-country-prompt': t('select-country.prompt'),
      'success-title': t('success.title'),
      'success-prompt': t('success.prompt'),
      'success-btn-submit': t('success.btn-submit'),
      'failed-title': t('failed.title'),
      'failed-prompt': t('failed.prompt'),
      'failed-btn-submit': t('failed.btn-submit'),
      'verify-government-id-select-title': t('select.title'),
      'verify-government-id-select-prompt': t('select.prompt'),
    });
    return templateWithPatchedCopy;
    // Intentionally don't include t in dependency array.
    // This prevents the text from flashing in the timespan between when copy is reloaded (`i18n.reloadResources`) and new copy is loaded (`fetchAndAddTranslation`).
    // We use `customTranslations` instead to recompute every time a new translation file is fetched.
  }, [selectedTemplate, i18n.language, customTranslations]);

  const templateLiveEdited = mergeResourceWithFormValues<Template>(
    templateSelectedPristine,
    props.formValues.template,
  );

  // Set custom copy on initial load and on template change. Does not set custom copy on save.
  useApiLoader({
    loader: async () => {
      if (templateSelectedPristine?.id != null && props.locale != null) {
        // Have to reload to handle removal of custom copy across templates
        await i18n.reloadResources(props.locale, 'translation');

        const file = templateSelectedPristine?.attributes['custom-translations']?.find(
          (file) => file.lang === props.locale,
        );
        if (file) {
          await fetchAndAddTranslation(file['translation-url'], i18n, props.locale).then(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setCustomTranslations,
          );
        }

        await i18n.changeLanguage(props.locale);
      }
    },
    deps: [templateSelectedPristine?.id, props.locale],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Could not load translations.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('An error occurred. Please try again.', MessageType.Failure),
  });

  const { loading: loadingTemplates } = useApiLoader({
    loader: props.loadNewAndLegacyInquiryTemplateList,
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Could not load templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('An error occurred. Please try again.', MessageType.Failure),
  });

  const { loading: loadingThemes } = useApiLoader({
    loader: props.loadThemeList,
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Could not load themes.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('An error occurred. Please try again.', MessageType.Failure),
  });

  const [selectedThemeId, setSelectedThemeId] = useState(null);
  const themeSelectedPristine =
    props.themes.find((t) => t.id === selectedThemeId) ||
    props.themes.find((t) => t.id === paramSelectedThemeId) ||
    head(props.themes);
  const themeLiveEdited = mergeResourceWithFormValues<Theme>(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    themeSelectedPristine,
    props.formValues.theme,
  );

  const { error: saveError, callback: handleSave } = useApiCallback(async () => {
    props.clearMessages();

    const updateTemplateLoader = async () => {
      const updatedTemplate = (
        await SessionClientDashboard.updateResource<Template>(templateLiveEdited, {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          meta: { locale: props.locale },
        })
      ).data;

      const file = updatedTemplate?.attributes['custom-translations']?.find(
        (file) => file.lang === props.locale,
      );
      if (file) {
        // Have to reload on save to handle removal of custom copy
        await i18n.reloadResources(props.locale, 'translation');
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        await fetchAndAddTranslation(file['translation-url'], i18n, props.locale).then(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          setCustomTranslations,
        );
      }

      // TODO: it might be better to change this to follow the index/show pattern, and just reload this specific
      // template instead of splicing the new state into the template list state.
      props.setTemplate(updatedTemplate);
    };

    const updateThemeLoader = async () => {
      const processedTheme = await processThemeLogo(themeLiveEdited as ThemeWithLogoFile);
      const hasLogo =
        ('logo-filename' in processedTheme.attributes &&
          'logo-data' in processedTheme.attributes) ||
        ('logo-url' in processedTheme.attributes && processedTheme.attributes['logo-url']);
      if (!hasLogo) {
        removeThemeLogo(processedTheme);
      }
      const updatedProcessedTheme =
        await SessionClientDashboard.updateResource<Theme>(processedTheme);
      props.setTheme(updatedProcessedTheme.data);
    };

    await Promise.all([updateTemplateLoader(), updateThemeLoader()]);

    props.showMessage('Your changes were successfully saved.', MessageType.Success);
  }, [
    props.clearMessages,
    templateLiveEdited,
    props.locale,
    props.setTemplate,
    themeLiveEdited,
    props.setTheme,
    props.showMessage,
  ]);

  useApiErrorHandler({
    error: saveError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('An error occurred. Please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('An error occurred. Please try again.', MessageType.Failure),
  });

  const handleTemplateSelect = async (templateId: string) => {
    const selectedTemplate = props.templates.find((t) => t.id === templateId);
    props.setSelectedTemplateId(templateId);

    // Make sure we're using the new selected template's copy
    await i18n.reloadResources(props.locale, 'translation');
    const file = selectedTemplate?.attributes['custom-translations']?.find(
      (file) => file.lang === props.locale,
    );
    if (file) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await fetchAndAddTranslation(file['translation-url'], i18n, props.locale).then(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setCustomTranslations,
      );
    }
  };

  const handleThemeSelect = (themeId: string) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setSelectedThemeId(themeId);
  };

  const currentLogoUrl = props.formValues.theme ? props.formValues.theme['logo-url'] : null;

  return (
    <DashboardEditorView
      productFeatureIsDisabled={productFeatureIsDisabled}
      handleSave={handleSave}
      handleSetLocale={props.setLocale}
      handleTemplateSelect={handleTemplateSelect}
      handleThemeSelect={handleThemeSelect}
      hasUnsavedChanges={props.hasUnsavedChanges}
      currentLogoUrl={currentLogoUrl}
      loading={loadingThemes || loadingTemplates}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      locale={props.locale}
      locationPathname={locationPathname}
      templateLiveEdited={templateLiveEdited}
      templates={props.templates}
      templateSelectedPristine={templateSelectedPristine}
      themeLiveEdited={themeLiveEdited}
      themes={props.themes}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      themeSelectedPristine={themeSelectedPristine}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    // TODO: type formValues properly
    formValues: (getFormValues('editor-theme')(state) ??
      getFormValues(FORM_THEME_EDITOR_ADVANCED_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_BUTTONS_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_COLOR_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_COMPLETION_PAGE_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_FAILED_PAGE_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_FONT_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_SELECT_COUNTRY_PAGE_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_DOCUMENTS_UPLOAD_PAGE_NAME)(state) ??
      getFormValues(FORM_THEME_EDITOR_START_PAGE_NAME)(state) ??
      {}) as any,
    templates: state.organization.templates,
    themes: state.organization.themes,
    locale: state.editor.locale,
    selectedTemplateId: state.editor.selectedTemplateId,
    currentUser: state.currentUser.currentUser,
    hasUnsavedChanges: isThemeEditorFormDirty(state),
    organization: state.organization.organization,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
  };
};

const mapDispatchToProps = {
  loadNewAndLegacyInquiryTemplateList,
  loadThemeList,
  setLocale,
  setTemplate,
  setTheme,
  setSelectedTemplateId,
  clearMessages,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardEditorRoute);
